import { COMMERCIAL } from '@/components/forms/CreateNewBusinessForm/validationSchema';
import {
	buildUnlayerClientContext,
	buildUnlayerDealContext,
	buildUnlayerUserContext,
	getClientIndustryId,
} from '@/components/Drawers/AddUnlayerEmailDrawer/helpers';
import { getUnlayerTagData, getUnlayerTags } from '@/utils/unlayer';
import { useLazyGetClientPropertiesQuery } from '@/features/clients/clientsApi';
import { useLazyGetDealPoliciesQuery, useLazyGetDealRowQuery } from '@/features/deals/dealsApi';
import { useLazyGetClassesOfRiskQuery } from '@/features/classesOfRisk/classesOfRiskApi';
import { useLazyGetInsightClaimQuery } from '@/features/claims/ticketsApi';
import { useSelector } from 'react-redux';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { useLazyGetInsightObjectByIdQuery } from '@/features/properties/propertiesApi';
import { useCallback } from 'react';

/**
 * Resolves merge tags for outbound email using fresh client/deal lookups at send time.
 * Shared between the send hook and any other code that needs send-time tag resolution.
 */
export const useResolveUnlayerMergeTags = () => {
	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;
	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const [loadInsightObjectById] = useLazyGetInsightObjectByIdQuery();
	const [loadClientProperties] = useLazyGetClientPropertiesQuery();
	const [loadDealRow] = useLazyGetDealRowQuery();
	const [loadDealPolicies] = useLazyGetDealPoliciesQuery();

	const [loadClassesOfRisk] = useLazyGetClassesOfRiskQuery();
	const [loadInsightClaim] = useLazyGetInsightClaimQuery();

	const resolveUnlayerMergeTags = useCallback(
		async (values) => {
			const unlayerUserContext = buildUnlayerUserContext({
				selectedFrom: values.from,
				userDetails,
				fallbackEmail: email,
			});

			const selectedClientId = values.client?.clientId ?? null;
			let selectedClientDetails = values.client ?? null;

			if (selectedClientId) {
				try {
					const clientProperties = await loadClientProperties(selectedClientId).unwrap();
					selectedClientDetails = {
						clientRow: {
							...(values.client ?? {}),
							...(clientProperties?.clientRow ?? {}),
						},
						insight: clientProperties?.insight,
					};
				} catch (error) {
					// Fall back to the selected client payload if the full lookup fails.
				}
			}

			const selectedDealId = values.deal?.dealId ?? null;
			let selectedDealDetails = values.deal ?? null;

			if (selectedDealId) {
				try {
					const dealRow = await loadDealRow(selectedDealId).unwrap();
					const policyData = await loadDealPolicies({
						dealId: selectedDealId,
						showLatest: true,
					})
						.unwrap()
						.catch(() => undefined);

					selectedDealDetails = {
						...(dealRow ?? {}),
						...(values.deal ?? {}),
						policies: policyData ?? [],
					};
				} catch (error) {
					// Fall back to the selected deal payload if the full lookup fails.
				}
			}

			const isClaimContext = Boolean(
				selectedDealDetails?.isClaim ||
				selectedDealDetails?.pipeline === process.env.REACT_APP_PIPELINE_CLAIMS,
			);

			if (selectedDealId && isClaimContext) {
				try {
					const insightClaim = await loadInsightClaim(selectedDealId).unwrap();
					selectedDealDetails = {
						...(selectedDealDetails ?? {}),
						...(insightClaim ?? {}),
					};
				} catch (error) {
					// Fall back to deal payload if the insight-claim lookup fails.
				}
			}

			let classesOfRisk = [];
			if (selectedDealDetails && userDetails) {
				try {
					classesOfRisk = await loadClassesOfRisk({
						countryCode: userDetails?.broker?.country ?? 'NZ',
						clientType: selectedDealDetails?.clientType ?? COMMERCIAL,
						brokerGroupIds: userDetails?.brokerGroupArray ?? [],
					}).unwrap();
				} catch (error) {
					// Fall back to unmapped risk class codes if this lookup fails.
				}
			}

			let industry;
			const industryId = getClientIndustryId(selectedClientDetails);
			if (industryId) {
				try {
					industry = await loadInsightObjectById(
						{ objectType: 'IndustryType', id: industryId },
						true,
					).unwrap();
				} catch (error) {
					// Fall back to the raw industry id if this lookup fails.
				}
			}

			const unlayerClientContext = buildUnlayerClientContext({
				clientDetails: selectedClientDetails,
				industryName: industry?.Name,
			});

			const unlayerDealContext = buildUnlayerDealContext({
				dealDetails: selectedDealDetails,
				classesOfRisk,
				policies: selectedDealDetails?.policies ?? [],
			});

			const tagData = getUnlayerTagData({
				user: unlayerUserContext,
				client: unlayerClientContext,
				deal: unlayerDealContext,
			});
			const mergeTags = getUnlayerTags(
				unlayerUserContext,
				unlayerClientContext,
				unlayerDealContext,
			);

			return {
				mergeTags,
				tagData,
				selectedClientDetails,
				selectedDealDetails,
			};
		},
		[
			loadClassesOfRisk,
			loadClientProperties,
			loadDealPolicies,
			loadDealRow,
			loadInsightClaim,
			loadInsightObjectById,
			userDetails,
			email,
		],
	);

	return resolveUnlayerMergeTags;
};
